import { useRef, useMemo, useEffect } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

const smoothstep = (edge0, edge1, x) => {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)))
  return t * t * (3 - 2 * t)
}

// ---------------------------------------------------------------------------
// Vertex shader — passes world position as a per-particle noise seed
// ---------------------------------------------------------------------------
const vertexShader = /* glsl */ `
  attribute float aAlpha;
  attribute float aSize;
  varying float vAlpha;
  varying vec2  vSeed;

  void main() {
    vAlpha = aAlpha;
    // Unique seed per particle: scale XZ so nearby particles get very different seeds
    vSeed  = position.xz * 1.73 + position.yy * 0.91;
    vec4 mvPos = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = aSize * (12000.0 / -mvPos.z);
    gl_Position  = projectionMatrix * mvPos;
  }
`

// ---------------------------------------------------------------------------
// Fragment shader — high-contrast FBM noise textures each smoke puff
// ---------------------------------------------------------------------------
const fragmentShader = /* glsl */ `
  varying float vAlpha;
  varying vec2  vSeed;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash(i),                   hash(i + vec2(1.0, 0.0)), f.x),
      mix(hash(i + vec2(0.0, 1.0)),  hash(i + vec2(1.0, 1.0)), f.x),
      f.y
    );
  }
  float fbm(vec2 p) {
    float v = 0.0, a = 0.5;
    for (int i = 0; i < 5; i++) { v += a * vnoise(p); p *= 2.1; a *= 0.5; }
    return v;
  }

  void main() {
    vec2  uv   = gl_PointCoord - 0.5;
    float dist = length(uv);

    // Radial falloff — soft edge
    float falloff = exp(-dist * dist * 4.0) * (1.0 - smoothstep(0.20, 0.50, dist));
    if (falloff < 0.004) discard;

    // Sample FBM at low frequency (2.5 cycles across sprite) — big, billowy features
    float noise = fbm(gl_PointCoord * 2.5 + vSeed);

    // High contrast stretch: map [0.35, 0.75] → [0, 1] so we get real light & dark pockets
    noise = clamp((noise - 0.35) / 0.40, 0.0, 1.0);

    // Smoke colour: pure white throughout
    float grey = mix(0.88, 1.00, noise);

    // Noise breaks up the alpha silhouette so edges are ragged, not a smooth disc
    float finalAlpha = vAlpha * falloff * mix(0.15, 1.0, noise);

    gl_FragColor = vec4(vec3(grey), finalAlpha);
  }
`

// ---------------------------------------------------------------------------
// MissileSmokeEmitter
//
// The <points> mesh sits at scene root with no parent transform.
// Particle positions are pure world-space — written directly to the GPU buffer.
//
// boneObject          — the raw THREE.Object3D (nodes.root) — read via
//                       matrixWorld each frame; never added to the scene graph
// nozzleLocalPosition — offset from the bone origin to the nozzle, in bone
//                       local space
// ---------------------------------------------------------------------------
export function MissileSmokeEmitter({
  boneObject,
  nozzleLocalPosition  = [0.0, -1.65, 0],
  rate                 = 5000,
  maxParticles         = 1500,
  startDelay           = 0.0,
  lifetimeFadeOutDuration = 2.5,   // seconds over which new particle lifetime ramps 1 → 0
}) {
  const meshRef    = useRef()
  const spawnAccum = useRef(0)
  const elapsed    = useRef(0)

  const state = useMemo(() => {
    const positions = new Float32Array(maxParticles * 3)
    const vel       = new Float32Array(maxParticles * 3)
    const ages      = new Float32Array(maxParticles)
    const lifetimes = new Float32Array(maxParticles)
    const alive     = new Uint8Array(maxParticles)
    const sizes     = new Float32Array(maxParticles)
    const alphas    = new Float32Array(maxParticles)
    return { positions, vel, ages, lifetimes, alive, sizes, alphas }
  }, [maxParticles])

  const geometry = useMemo(() => {
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.BufferAttribute(state.positions, 3))
    g.setAttribute('aSize',    new THREE.BufferAttribute(state.sizes,     1))
    g.setAttribute('aAlpha',   new THREE.BufferAttribute(state.alphas,    1))
    return g
  }, [state])

  const material = useMemo(() => new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    transparent: true,
    depthWrite:  false,
    blending:    THREE.NormalBlending,
  }), [])

  useEffect(() => () => {
    geometry.dispose()
    material.dispose()
  }, [geometry, material])

  // Scratch objects — never reallocated
  const _nozzleLocal   = useMemo(
    () => new THREE.Vector3(...nozzleLocalPosition),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nozzleLocalPosition[0], nozzleLocalPosition[1], nozzleLocalPosition[2]]
  )
  const _nozzleWorld   = useMemo(() => new THREE.Vector3(), [])
  const _boneQuat      = useMemo(() => new THREE.Quaternion(), [])
  const _backwardWorld = useMemo(() => new THREE.Vector3(), [])
  const _radialDir     = useMemo(() => new THREE.Vector3(), [])
  const _perpAxis      = useMemo(() => new THREE.Vector3(), [])

  useFrame((_, delta) => {
    elapsed.current += delta
    if (elapsed.current < startDelay) return
    if (!boneObject) return

    const { positions, vel, ages, lifetimes, alive, sizes, alphas } = state
    const n = maxParticles

    // Lifetime multiplier ramps from 1 → 0 over lifetimeFadeOutDuration seconds
    const activeTime      = elapsed.current - startDelay
    const lifetimeMult    = lifetimeFadeOutDuration > 0
      ? Math.max(0, 1.0 - activeTime / lifetimeFadeOutDuration)
      : 1.0

    boneObject.updateWorldMatrix(true, false)
    _nozzleWorld.copy(_nozzleLocal).applyMatrix4(boneObject.matrixWorld)
    boneObject.getWorldQuaternion(_boneQuat)
    // local -Y points backward (away from nose) in bone space
    _backwardWorld.set(0, -1, 0).applyQuaternion(_boneQuat)

    // --- Spawn ---
    spawnAccum.current += rate * delta
    while (spawnAccum.current >= 1) {
      spawnAccum.current -= 1

      let slot = -1
      for (let i = 0; i < n; i++) {
        if (!alive[i]) { slot = i; break }
      }
      if (slot === -1) break

      const i3 = slot * 3
      positions[i3]     = _nozzleWorld.x + (Math.random() - 0.5) * 0.5
      positions[i3 + 1] = _nozzleWorld.y + (Math.random() - 0.5) * 0.5
      positions[i3 + 2] = _nozzleWorld.z + (Math.random() - 0.5) * 0.5

      // Backward component — scaled to match scene (F35 scale=10 in Experience)
      const axialSpeed = 1000.0 + Math.random() * 1500.0   // 1000–2500 world units/s

      // Radial burst — tight perpendicular spread
      _perpAxis.set(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5)
      _radialDir.crossVectors(_backwardWorld, _perpAxis).normalize()
      const radialSpeed = 2.0 + Math.random() * 5.0   // 2–7 world units/s outward

      vel[i3]     = _backwardWorld.x * axialSpeed + _radialDir.x * radialSpeed
      vel[i3 + 1] = _backwardWorld.y * axialSpeed + _radialDir.y * radialSpeed
      vel[i3 + 2] = _backwardWorld.z * axialSpeed + _radialDir.z * radialSpeed

      ages[slot]      = 0
      lifetimes[slot] = Math.max(0.01, (0.25 + Math.random() * 0.5) * lifetimeMult)
      alive[slot]     = 1
      sizes[slot]     = 0
      alphas[slot]    = 0
    }

    // --- Update ---
    // Very light drag — 2% per frame at 60fps, so particles travel most of their velocity
    const drag = Math.pow(0.98, delta * 60)

    for (let i = 0; i < n; i++) {
      if (!alive[i]) {
        sizes[i]  = 0
        alphas[i] = 0
        continue
      }

      ages[i] += delta
      const t = ages[i] / lifetimes[i]

      if (t >= 1.0) {
        alive[i]  = 0
        sizes[i]  = 0
        alphas[i] = 0
        continue
      }

      const i3 = i * 3

      // Apply gentle drag
      vel[i3]     *= drag
      vel[i3 + 1] *= drag
      vel[i3 + 2] *= drag

      // Integrate world-space position
      positions[i3]     += vel[i3]     * delta
      positions[i3 + 1] += vel[i3 + 1] * delta
      positions[i3 + 2] += vel[i3 + 2] * delta

      sizes[i] = 0.25 + t * 1.0

      const fadeOut = 1.0 - smoothstep(0.0, 1.0, t)
      alphas[i] = fadeOut * 0.40
    }

    geometry.attributes.position.needsUpdate = true
    geometry.attributes.aSize.needsUpdate    = true
    geometry.attributes.aAlpha.needsUpdate   = true
    geometry.computeBoundingSphere()
  })

  return (
    <points
      ref={meshRef}
      geometry={geometry}
      material={material}
      frustumCulled={false}
    />
  )
}
